import React, { useEffect, useId, useRef } from 'react';
import { Modal } from 'antd';
import { AlertTriangle, CircleHelp, Info, Trash2 } from 'lucide-react';
import { useOptionalLanguage } from '../../context/LanguageContext';
import { crmTypography } from '../../components/crm/crmPalette';
import { translateUiText } from '../locale/centralLocalization';
import { cycleOverlayTab } from '../overlayFocus';
import type { ConfirmOptions } from './ConfirmContext';
import './ConfirmProvider.css';

export interface ConfirmationDialogProps {
  open: boolean;
  title: React.ReactNode;
  message: React.ReactNode;
  children?: React.ReactNode;
  busy?: boolean;
  confirmDisabled?: boolean;
  error?: React.ReactNode;
  variant?: ConfirmOptions['variant'];
  confirmLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  closeLabel?: string;
  language?: 'ar' | 'en';
  onConfirm: () => void;
  onCancel: () => void;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
}

/** Direction B presentation for imperative and controlled/public consumers. */
export function ConfirmationDialog({ open, title, message, children, busy = false, error,
  confirmDisabled = false, variant = 'standard', confirmLabel, cancelLabel, closeLabel, language: requestedLanguage,
  onConfirm, onCancel, closeOnBackdrop = true, closeOnEscape = true,
}: ConfirmationDialogProps) {
  const appLocale = useOptionalLanguage();
  const language = requestedLanguage ?? appLocale?.language ?? 'en';
  const t = (value: string) => translateUiText(value, language);
  const messageId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const clicked = useRef(false);
  useEffect(() => { if (!open || !busy) clicked.current = false; }, [open, busy]);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.querySelector<HTMLElement>('[data-confirm-cancel]')?.focus();
    return () => { if (previous?.isConnected) previous.focus(); };
  }, [open]);
  const cancel = () => { if (!busy) onCancel(); };
  const Icon = { standard: CircleHelp, warning: AlertTriangle, destructive: Trash2, info: Info }[variant];
  return <Modal open={open} title={<><span className="app-confirmation__icon" aria-hidden="true"><Icon size={24}/></span><span>{title}</span></>}
    footer={null} className={`app-confirmation app-confirmation--${variant}`} style={{ fontFamily: `"${crmTypography[language]}", sans-serif` }}
    aria-describedby={messageId} transitionName="" maskTransitionName=""
    closable={{ 'aria-label': closeLabel ?? t('Close confirmation'), disabled: busy }}
    maskClosable={!busy && closeOnBackdrop} keyboard={!busy && closeOnEscape}
    zIndex={2147483646} width="min(380px, calc(100vw - 32px))" centered destroyOnHidden
    onCancel={event => { if (!event.defaultPrevented) cancel(); }}
    afterOpenChange={visible => { if (visible) {
      dialogRef.current?.closest('[role="dialog"]')?.setAttribute('aria-describedby', messageId);
      dialogRef.current?.querySelector<HTMLElement>('[data-confirm-cancel]')?.focus();
    } }}
    modalRender={node => <div ref={dialogRef} dir={language === 'ar' ? 'rtl' : 'ltr'} lang={language}
      data-testid="confirm-backdrop" role="button" tabIndex={-1} aria-label={t('Dismiss confirmation backdrop')}
      onClick={event => { if (closeOnBackdrop && event.target === event.currentTarget) cancel(); }}
      onKeyDown={event => {
        if (event.defaultPrevented) return;
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); if (closeOnEscape) cancel(); }
        cycleOverlayTab(event);
      }}>{node}</div>}>
    <div id={messageId} className="app-confirmation__message">{message}</div>
    {children}
    {error && <div role="alert" className="app-confirmation__message" style={{ color: 'var(--crm-danger-text)' }}>{error}</div>}
    <div className="app-confirmation__actions">
      <button type="button" className="app-confirmation__cancel" data-confirm-cancel disabled={busy} onClick={cancel}>{cancelLabel ?? t('Cancel')}</button>
      <button type="button" className="app-confirmation__accept" disabled={busy || confirmDisabled} aria-busy={busy}
        onClick={() => { if (busy || confirmDisabled || clicked.current) return; clicked.current = true; onConfirm(); }}>{confirmLabel ?? t('Yes, Proceed')}</button>
    </div>
  </Modal>;
}
